import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import paper from 'paper';
import { shuffle } from 'lodash';
import { scaleSVG } from 'utils/managers/svg-manager';
import { resizeCanvas } from 'utils/managers/canvas-manager';
import { ErrorTypes } from 'constants/error-types';
import guidelineManager from 'utils/managers/guideline-manager';
import pointer2 from 'assets/OldWDT/photo/draw-stage/crayon-pointer-OB.png';
import smileyPointerPath from 'assets/OldWDT/photo/draw-stage/Smiley_200x200_wbg2.png';
import arrowPointerPath from 'assets/OldWDT/photo/draw-stage/Arrow_200x200_wbg2.png';
import pointerTexture from 'assets/OldWDT/photo/draw-stage/chalk-texture3-blue.png';
import DistanceManager from 'utils/managers/distance-manager';
import TimeoutList from 'utils/timeout-list';
import { showVisualCue, hideVisualCue } from 'store/actions/ui';
import { t } from '../../translations';
import './draw-activity-stage.scss';

const TIMEOUT = process.env.REACT_APP_INACTIVITY_TIMEOUT;

const REASON = {
  incorrectDraw3Times: 'Incorrect draw for 3 times'
};

export class CrayonStrokeGame extends Component {
  constructor(props) {
    super(props);
    this._canvas = null;
    this.drawEnabled = false;
    this.advancedToSecondCharacter = false;

    this.transportPathDrawn = 0;
    this.touchId = null;
    this.flags = [];
    this.drawConfig = {
      density: 10,
      timeInterval: 40
    };
    this.formerFlagIndex = -1;
    this.characterSvgPath = null;
    this.isFullScreen = false;
    this.guideLineIndex = -1;
    this.consecutiveErrorsAmount = 0;
    this.helpInProgress = false;
    this.guideLineSVGs = null;
    this.textureTrails = [];
    this.currentDrawInstrument = null;
    this.textureGroup = null;
    this.textureDegrees = [15, 30, 45, 60, 75, 90, -15, -30, -45, -60, -75, -90];
    this.timeoutList = new TimeoutList();
    this.flagOpacity = 0;
    this.previousCursorPosition = {
      x: null,
      y: null
    };
    this.drawnDotPath = false;
    this.dotDrawStartValid = false;

    this.drawInstrumentStates = [
      {
        texturePath: pointerTexture,
        pointerPath: pointer2,
        name: 'chalk2'
      }
    ];
    this.pointerSoundPlaying = false;
    this.timers = [];

    this.currentTolerance = 0;
    this.distanceManager = new DistanceManager(0, 0, 10);
    this.handleSVGLoad = this.handleSVGLoad.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleDraw = this.handleDraw.bind(this);
    this.handleDrawEnd = this.handleDrawEnd.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.handleOutOfCanvasBounds = this.handleOutOfCanvasBounds.bind(this);

    this.cancelDrawAnimation = false;

    this.state = {
      roundIndex: -1,
      helpers: {
        arrow: {
          show: false,
          x: 0,
          y: 0,
          width: 90,
          height: 90
        },
        smiley: {
          show: false,
          x: 0,
          y: 0,
          width: 90,
          height: 90
        }
      }
    };
  }

  async componentDidMount() {
    const {
      audio,
      data: { crayonDrawing }
    } = this.props.currentRound;
    const chalkSound = audio[crayonDrawing.src];
    this.characterSvgPath = this.props.svgSrc;

    chalkSound.on('play', () => {
      this.pointerSoundPlaying = true;
    });
    chalkSound.on('end', () => {
      this.pointerSoundPlaying = false;
    });
    chalkSound.on('pause', () => {
      this.pointerSoundPlaying = false;
    });
    chalkSound.on('stop', () => {
      this.pointerSoundPlaying = false;
    });
    this.nextRound();

    this.props.resetAutoScroll();
  }

  componentWillUnmount() {
    const {
      audio,
      data: { crayonDrawing }
    } = this.props.currentRound;

    audio[crayonDrawing.src] && audio[crayonDrawing.src].stop();

    this.currentDrawInstrument && this.currentDrawInstrument.remove();
    if (paper.project) {
      paper.project.clear();
      paper.project.remove();
    }
    if (this._canvas) {
      const context = this._canvas.getContext('2d');
      context.clearRect(0, 0, this._canvas.width, this._canvas.height);
      this._canvas.remove();
    }
    this.timeoutList.cancelAll();
    this.removeTouchEventListeners();
    this.timers.length && this.timers.forEach(tID => clearTimeout(tID));

    this.cancelDrawAnimation = true;
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.roundIndex !== this.state.roundIndex) {
      this.setupCanvas(this._canvas);
    }

    if (prevProps.zoomToleranceFactor !== this.props.zoomToleranceFactor) {
      this.computeTolerance();
    }
  }

  addVisualCue(message, interval = 1000) {
    this.props.showVisualCue(message);
    this.timers.push(setTimeout(() => this.props.hideVisualCue(), interval));
  }

  removeFlagsFromPath() {
    this.flags.forEach(flag => {
      flag.left.remove();
      flag.right.remove();
    });
    this.flags = [];
  }

  getFlagIndex(currentGuideLineSVG, flags) {
    if (currentGuideLineSVG.isReversed) {
      return flags.length;
    }

    return 0;
  }

  setupCanvas(currentCanvas) {
    paper.setup(currentCanvas);
    paper.project.importSVG(this.props.svgSrc, { onLoad: this.handleSVGLoad });
  }

  addTouchEventListeners() {
    this._canvas.addEventListener('mousedown', this.handleTouchStart);
    this._canvas.addEventListener('mousemove', this.handleDraw);
    this._canvas.addEventListener('mouseup', this.handleTouchEnd);
    this._canvas.addEventListener('touchstart', this.handleTouchingStart);
    this._canvas.addEventListener('touchmove', this.handleTouchDraw);
    this._canvas.addEventListener('touchend', this.handleTouchingEnd);
    this._canvas.addEventListener('mouseout', this.handleOutOfCanvasBounds);
  }

  handleContinuousPathDrawInvalid() {
    const pathAmount = this.guideLineSVGs[this.guideLineIndex].removeSegments;

    this.removeTextureTrail(this.guideLineIndex, pathAmount);
    this.resetPathState(pathAmount);
  }

  handleDotGuideLineValidDrawStart(clientX, clientY) {
    if (!this.drawnDotPath) {
      this.dotDrawStartValid = true;
      this.toggleDrawInstrumentOpacity(true);
      this.scaleDrawInstrument();

      if (this.drawInstrumentStates[this.state.roundIndex].name === 'chalk2') {
        this.drawDenseTexturePoint(clientX, clientY, this.guideLineIndex);
      } else {
        this.drawTexturePoint(clientX, clientY, this.guideLineIndex);
      }

      this.timeoutList.add(() => {
        // We need to set this flag to true in order to avoid
        // the bypassing of handleDrawEnd, when pressing out of
        // the dot tolerance range, after a successful tap / tap-drag draw
        this.drawEnabled = true;
        this.handleDrawEnd();
      }, 1000);
    }
  }

  drawDenseTexturePoint(x, y, guideLineIndex) {
    for (let i = 0; i < 3; i++) {
      this.drawTexturePoint(x, y, guideLineIndex);
    }
  }

  checkDrawingDirection(cX, cY) {
    let directionX, directionY, diffX, diffY;
    if (this.previousCursorPosition.x < cX) {
      directionX = 'right';
      diffX = cX - this.previousCursorPosition.x;
    } else {
      directionX = 'left';
      diffX = this.previousCursorPosition.x - cX;
    }
    if (this.previousCursorPosition.y < cY) {
      directionY = 'down';
      diffY = cY - this.previousCursorPosition.y;
    } else {
      directionY = 'up';
      diffY = this.previousCursorPosition.y - cY;
    }
    this.setPreviousCursorPosition(cX, cY);
    if (diffX > diffY) {
      return directionX;
    } else {
      return directionY;
    }
  }

  processDrawEvent(event) {
    let clientX;
    let clientY;
    let cursorPosition = this.getCursorPosition(event);
    const pressedMouseButton = event.buttons !== undefined ? event.buttons : event.which;
    const isTouchEvent = event.constructor.name === 'TouchEvent';

    clientX = cursorPosition.clientX;
    clientY = cursorPosition.clientY;

    const currentGuideLine = this.guideLineSVGs[this.guideLineIndex];
    const needsValidation = this.distanceManager.checkThreshold(clientX, clientY);

    const isDrawing =
      this.drawEnabled &&
      !this.helpInProgress &&
      needsValidation &&
      (pressedMouseButton || isTouchEvent);

    return {
      clientX,
      clientY,
      currentGuideLine,
      isDrawing
    };
  }

  handleDraw(event) {
    const { clientX, clientY, currentGuideLine, isDrawing } = this.processDrawEvent(event);
    const cursorPoint = new paper.Point(clientX, clientY);

    this.props.handleAutoScroll(clientY);

    if (isDrawing) {
      this.playTraceSound();
      const direction = this.checkDrawingDirection(clientX, clientY);

      if (currentGuideLine.isDot) {
        this.handleDrawOverDotPath(clientX, clientY, currentGuideLine);
      } else {
        this.drawTexturePoint(clientX, clientY, this.guideLineIndex);
        this.moveDrawInstrument(clientX, clientY);
      }

      if (!currentGuideLine.isDot) {
        if (!this.validateDrawDirection(currentGuideLine, clientX, clientY)) {
          this.handleInvalidDrawDirection(currentGuideLine, direction);
        }

        if (!this.validateDrawStray(currentGuideLine, clientX, clientY, this.currentTolerance)) {
          this.handleInvalidDrawStray(currentGuideLine, direction);
        }
        this.catchEndOfPath(cursorPoint, event);
      }
    }
  }

  handleDrawEnd() {
    const {
      audio,
      data: { crayonDrawing }
    } = this.props.currentRound;

    audio[crayonDrawing.src] && audio[crayonDrawing.src].pause();
    if (this.drawEnabled) {
      if (this.guideLineIndex < this.guideLineSVGs.length - 1) {
        this.setNextPathState();
        if (
          this.guideLineIndex < this.guideLineSVGs.length &&
          !this.guideLineSVGs[this.guideLineIndex].isContinuePath
        ) {
          this.addVisualCue('success');
          this.drawEnabled = false;
          this.props.sndCorrect.play();
          this.props.sndCorrect.on('end', () => {
            if (this.guideLineSVGs[this.guideLineIndex].isTransportPath) {
              this.handleTransportPathProgress();
              this.transportPathDrawn++;
              if (this.props.character.length > 1 && this.transportPathDrawn === 1) {
                this.advancedToSecondCharacter = true;
              }
            } else {
              this.resetDrawInstrumentPosition();
            }
            this.props.sndCorrect.off();
          });
        }
      } else {
        this.drawEnabled = false;
        this.addVisualCue('success');
        this.props.sndCorrect.play();
        this.props.sndCorrect.on('end', () => {
          this.advance();
          this.props.sndCorrect.off();
        });
      }
    }
  }

  calculatePrecentDone(currentGuideLine) {
    const segments = [];
    this.textureTrails[this.guideLineIndex].forEach(raster => {
      const segment = [raster.position.x, raster.position.y];
      segments.push(segment);
    });
    const drawnPath = new paper.Path({
      segments: segments,
      strokeColor: 'white',
      visible: false,
      selected: false
    });
    const qty = (drawnPath.length / currentGuideLine.length) * 100;
    drawnPath.remove();
    return qty;
  }

  constructAndLogError(currentGuideLine, errorType, direction) {
    const timeStamp = new Date().toISOString();
    const error = {};
    const percentComplete = parseInt(this.calculatePrecentDone(currentGuideLine));
    switch (errorType.name) {
      case 'OutOfBounds':
        error.error = errorType.name;
        error.competency = 'Writing';
        error.actual = {
          direction: direction,
          percentComplete: percentComplete
        };
        error.expected = {
          character: this.advancedToSecondCharacter
            ? this.props.character[1]
            : this.props.character[0],
          stroke: currentGuideLine.name
        };
        if (percentComplete < 10) {
          // if its out of bounds in the first 10% its actually starting the wrong way
          error.error = ErrorTypes.STARTED_WRONG_WAY.name;
          // TODO: add direction from svg
          // error.expected.direction = this.props.svgNameList[currentGuideLine.name].direction;
        }
        error.timestamp = timeStamp;
        this.props.logError(error);
        break;
      case 'ReversedDirection':
        error.error = errorType.name;
        error.competency = 'Writing';
        error.actual = {
          direction: direction,
          percentComplete: percentComplete
        };
        error.expected = {
          character: this.advancedToSecondCharacter
            ? this.props.character[1]
            : this.props.character[0],
          stroke: currentGuideLine.name
          // TODO: add direction from svg
          // direction: this.props.svgNameList[currentGuideLine.name].direction
        };
        error.timestamp = timeStamp;
        this.props.logError(error);
        break;
      case 'LiftedFinger':
        error.error = errorType.name;
        error.competency = 'Writing';
        error.actual = {
          percentComplete: percentComplete
        };
        error.expected = {
          character: this.advancedToSecondCharacter
            ? this.props.character[1]
            : this.props.character[0],
          stroke: currentGuideLine.name
        };
        error.timestamp = timeStamp;
        this.props.logError(error);
        break;
    }
  }

  handleDrawEndInvalid(currentGuideLine, errorType, direction) {
    const {
      props: {
        currentRound: {
          audio,
          data: {
            instructions: { beforeDraw },
            crayonDrawing
          }
        }
      },
      state: {
        helpers: { arrow, smiley }
      }
    } = this;

    this.constructAndLogError(currentGuideLine, errorType, direction);
    this.consecutiveErrorsAmount++;
    if (this.consecutiveErrorsAmount >= 3) {
      if (this.props.level === 3) {
        clearInterval(this.blinkingInstrument);
        this.toggleDrawInstrumentOpacity(true);
      }

      if (this.advancedToSecondCharacter) {
        const { audio } = this.props.currentRound;
        const { beforeDraw } = this.props.currentRound.data.instructions;

        this.helpInProgress = true;
        this.props.inactivityManager.clear();
        this.consecutiveErrorsAmount = 0;
        audio[beforeDraw[1].src].play();
        this.resetPath(currentGuideLine);
        const startingPos = this.getPathStartPoint(this.guideLineSVGs[this.guideLineIndex]);
        this.toggleDrawInstrumentOpacity(false);

        if (['10', '18', '19', '20'].includes(this.props.character)) {
          this.setHelperArrow({ x: startingPos.x, y: startingPos.y }, true);
        } else {
          this.setHelperSmiley({ x: startingPos.x, y: startingPos.y }, true);
        }

        audio[beforeDraw[1].src].on('end', () => {
          this.setState(state => ({
            ...state,
            helpers: {
              ...state.helpers,
              arrow: {
                ...arrow,
                show: false
              },
              smiley: {
                ...smiley,
                show: false
              }
            }
          }));

          this.toggleDrawInstrumentOpacity(true);
          this.getHelp(() => {
            this.helpInProgress = false;
            this.setDrawInstrumentVisibility();
            this.props.inactivityManager.watch(
              TIMEOUT,
              () => audio[beforeDraw[2].src].play(),
              REASON.incorrectDraw3Times,
              true
            );
          });

          audio[beforeDraw[1].src].off();
        });
      } else {
        this.consecutiveErrorsAmount = 0;
        this.helpInProgress = true;
        this.props.inactivityManager.clear();
        audio[beforeDraw[0].src].play();
        this.resetPath(currentGuideLine);
        const startingPos = this.getPathStartPoint(this.guideLineSVGs[this.guideLineIndex]);
        this.toggleDrawInstrumentOpacity(false);

        if (['8', '9'].includes(this.props.character)) {
          this.setHelperArrow({ x: startingPos.x, y: startingPos.y }, true);
        } else {
          this.setHelperSmiley({ x: startingPos.x, y: startingPos.y }, true);
        }

        audio[beforeDraw[0].src].on('end', () => {
          this.setState(state => ({
            ...state,
            helpers: {
              ...state.helpers,
              arrow: {
                ...arrow,
                show: false
              },
              smiley: {
                ...smiley,
                show: false
              }
            }
          }));
          this.toggleDrawInstrumentOpacity(true);
          this.getHelp(() => {
            this.helpInProgress = false;
            this.setDrawInstrumentVisibility();
            this.props.inactivityManager.watch(
              TIMEOUT,
              () => audio[beforeDraw[0].src].play(),
              REASON.incorrectDraw3Times,
              true
            );
          });
          audio[beforeDraw[0].src].off();
        });
      }
    } else {
      this.props.sndWrong.play();
      this.addVisualCue('error');
      audio[crayonDrawing.src].pause();
      this.resetPath(currentGuideLine);
      this.endPathNormal = this.placeEndPathNormalOnPath(this.guideLineSVGs[this.guideLineIndex]);
    }
  }

  handleDrawStart(event) {
    const { inactivityManager } = this.props;
    inactivityManager && inactivityManager.stopAudio();

    let clientX;
    let clientY;
    let cursorPosition = this.getCursorPosition(event);

    clientX = cursorPosition.clientX;
    clientY = cursorPosition.clientY;

    const currentGuideLine = this.guideLineSVGs[this.guideLineIndex];

    const cursorPoint = new paper.Point(clientX, clientY);
    let pathStart = null;

    this.drawEnabled = false;

    if (currentGuideLine.isDot) {
      pathStart = new paper.Point(
        currentGuideLine.getBounds().center.x,
        currentGuideLine.getBounds().center.y
      );
    } else {
      if (currentGuideLine.isReversed) {
        pathStart = currentGuideLine.getPointAt(currentGuideLine.length);
      } else {
        pathStart = currentGuideLine.getPointAt(0);
      }
    }

    if (cursorPoint.isClose(pathStart, this.currentTolerance)) {
      this.handleValidDrawStart(clientX, clientY, currentGuideLine);
    } else {
      this.consecutiveErrorsAmount++;
    }
  }

  handleDrawOverDotPath(clientX, clientY, currentGuideLine, index) {
    const pathStart = new paper.Point(
      currentGuideLine.getBounds().center.x,
      currentGuideLine.getBounds().center.y
    );

    const cursorPoint = new paper.Point(clientX, clientY);

    if (cursorPoint.isClose(pathStart, this.currentTolerance) && !this.drawnDotPath) {
      this.drawTextureCluster(clientX, clientY, this.guideLineIndex, index);
      this.moveDrawInstrument(clientX, clientY);
    }
  }

  handleOutOfCanvasBounds(event) {
    const { clientX, clientY, currentGuideLine, isDrawing } = this.processDrawEvent(event);

    if (isDrawing) {
      const direction = this.checkDrawingDirection(clientX, clientY);
      this.handleInvalidDrawStray(currentGuideLine, direction);
    }
  }

  handleInvalidDrawStray(currentGuideLine, direction) {
    this.drawEnabled = false;
    this.handleDrawEndInvalid(currentGuideLine, ErrorTypes.OUT_OF_BOUNDS, direction);
  }

  handleInvalidDrawDirection(currentGuideLine, direction) {
    this.drawEnabled = false;
    this.props.sndWrong.play();
    this.handleDrawEndInvalid(currentGuideLine, ErrorTypes.WRONG_WAY, direction);
    this.addVisualCue('error');
  }

  handleTouchingStart = event => {
    event.preventDefault();
    if (event.targetTouches.length >= 2) {
      console.log('Found and prevented multi-touch gesture on touchstart'); // eslint-disable-line no-console
      event.preventDefault();
    } else {
      this.handleTouchStart(event);
    }
  };

  handleTouchStart(event) {
    this.mouseTouchPressed = true;
    const { inactivityManager } = this.props;
    if (inactivityManager) {
      inactivityManager.clear();
      inactivityManager.stopAudio();
    }

    this.handleDrawStart(event);
  }

  handleTouchingEnd = event => {
    event.preventDefault();
    this.handleTouchEnd(event);
  };

  handleTouchDraw = event => {
    event.preventDefault();
    this.handleDraw(event);
  };

  handleTouchEnd(event) {
    let currentGuideLine = this.guideLineSVGs[this.guideLineIndex];

    this.mouseTouchPressed = false;
    if (currentGuideLine.isDot && !this.drawnDotPath && this.dotDrawStartValid) {
      this.drawnDotPath = true;
      this.dotDrawStartValid = false;
    }
    let cursorPoint;

    if (event.changedTouches) {
      cursorPoint = event.changedTouches[0];
    } else {
      cursorPoint = event;
    }

    if (
      !this.isMouseOrTouchPressed(event) &&
      !this.helpInProgress &&
      this.drawEnabled &&
      !this.endPositionIsValid(cursorPoint)
    ) {
      this.props.sndWrong.play();
      this.props.sndWrong.on('end', () => {
        currentGuideLine = this.guideLineSVGs[this.guideLineIndex];
        this.handleDrawEndInvalid(currentGuideLine, ErrorTypes.FINGER_UP);
        this.props.sndWrong.off();
      });
      this.addVisualCue('error');
    }
  }

  handleTransportPathProgress() {
    const currentGuideLine = this.guideLineSVGs[this.guideLineIndex];
    this.drawEnabled = false;
    this.drawPath(currentGuideLine, this.drawConfig.timeInterval).then(() => {
      this.nextPath();
    });
  }

  handleSVGLoad(characterSVG) {
    scaleSVG(characterSVG, this._canvas, this.isFullScreen);
    resizeCanvas(this._canvas, this._container);
    guidelineManager.getGuidelines(this.props.segments, paper.project, this.drawConfig.density);
    this.guideLineSVGs = guidelineManager.guidelineSVGs;
    const exposedData = [];
    this.guideLineSVGs.forEach(el => {
      const path = {
        name: el.name,
        coordinates: el.data.coordinates
      };
      exposedData.push(path);
    });
    // eslint-disable-next-line
    console.log(exposedData);
    this.addTouchEventListeners();
    this.textureGroup = this.initializeTextureGroup(this.isFullScreen);
    this.setNextRoundState();
  }

  handleValidDrawStart(clientX, clientY, currentGuideLine) {
    clearInterval(this.blinkingInstrument);

    this.drawEnabled = true;

    if (currentGuideLine.isDot) {
      this.handleDotGuideLineValidDrawStart(clientX, clientY);
    } else {
      this.timeoutList.cancelAll();
      this.toggleDrawInstrumentOpacity(true);
      this.scaleDrawInstrument();
    }
  }

  isMouseOrTouchPressed(event) {
    let pressed = false;
    let isWeb = event.touches === undefined;

    if (isWeb) {
      pressed = this.mouseTouchPressed;
    } else {
      pressed = event.touches && event.touches.length === 1;
    }

    return pressed;
  }

  scaleDrawInstrument() {
    let scale = 1.5;
    const currentScale = Math.floor(this.currentDrawInstrument.getScaling().length);
    const originalScale = Math.floor(this.currentDrawInstrument.originalScale);

    if (originalScale !== currentScale) {
      this.currentDrawInstrument.scale(1 / scale);
    } else {
      this.currentDrawInstrument.scale(scale);
      this.timeoutList.add(() => {
        // set 1/scale, to scale back to original size
        this.currentDrawInstrument.scale(1 / scale);
      }, 100);
    }
  }

  getHelp(onComplete) {
    const {
      audio,
      data: { crayonDrawing }
    } = this.props.currentRound;

    let currentGuideLine = null;
    let continuousPathData = null;

    this.drawPath(this.guideLineSVGs[this.guideLineIndex], this.drawConfig.timeInterval).then(
      () => {
        if (
          this.guideLineIndex < this.guideLineSVGs.length - 1 &&
          this.guideLineSVGs[this.guideLineIndex + 1].isContinuePath
        ) {
          //draw next continue path
          this.setNextPathState();
          this.getHelp(onComplete);
        } else {
          //reset to initial position
          currentGuideLine = this.guideLineSVGs[this.guideLineIndex];
          // TODO: is this equivalent to currentGuideLine.isContinuePath?
          continuousPathData = currentGuideLine.isContinuePath;

          if (continuousPathData) {
            this.handleContinuousPathDrawInvalid();
          }

          this.resetDrawInstrumentPosition();
          this.removeTextureTrail(this.guideLineIndex);
          this.resetFlagCache(this.guideLineSVGs[this.guideLineIndex]);
          this.endPathNormal = this.placeEndPathNormalOnPath(
            this.guideLineSVGs[this.guideLineIndex]
          );
          audio[crayonDrawing.src].pause();
          onComplete();
        }
      }
    );
  }

  resetPath(currentGuideLine) {
    const continuousPathData = currentGuideLine.isContinuePath;

    if (continuousPathData) {
      this.handleContinuousPathDrawInvalid();
    }

    this.resetDrawInstrumentPosition();
    this.removeTextureTrail(this.guideLineIndex);
    this.resetFlagCache(currentGuideLine);
  }

  resetFlagCache(guideLineSVG) {
    if (guideLineSVG.isReversed) {
      this.formerFlagIndex = this.flags.length;
    } else {
      this.formerFlagIndex = -1;
    }
  }

  removeTextureTrail(guideLineIndex, trailAmount = 1) {
    for (let i = 0; i < trailAmount; i++) {
      if (this.textureTrails[guideLineIndex - i]) {
        this.textureTrails[guideLineIndex - i].forEach(chalkPoint => {
          chalkPoint.remove();
        });
      }
      this.textureTrails[guideLineIndex - i] = [];
    }
  }

  resetPathState(pathAmount) {
    this.removeFlagsFromPath(this.guideLineSVGs[this.guideLineIndex]);
    this.guideLineIndex -= pathAmount - 1;
    this.flags = this.placeFlagsOnPath(this.guideLineSVGs[this.guideLineIndex]);
    this.formerFlagIndex = this.getFlagIndex(this.guideLineSVGs[this.guideLineIndex], this.flags);
    this.endPathNormal = this.placeEndPathNormalOnPath(this.guideLineSVGs[this.guideLineIndex]);
  }

  getCursorPosition(event) {
    let cursorPosition = { clientX: 0, clientY: 0 };
    const { left, top } = this._canvas.getBoundingClientRect();

    if (event.touches) {
      cursorPosition.clientX = event.touches[0].clientX - left;
      cursorPosition.clientY = event.touches[0].clientY - top;
    } else {
      cursorPosition.clientX = event.clientX - left;
      cursorPosition.clientY = event.clientY - top;
    }

    return cursorPosition;
  }

  setPreviousCursorPosition(clientX, clientY) {
    if (clientX !== undefined && clientY !== undefined) {
      this.previousCursorPosition.x = clientX;
      this.previousCursorPosition.y = clientY;
    } else {
      this.previousCursorPosition.x = null;
      this.previousCursorPosition.y = null;
    }
  }

  playTraceSound() {
    const {
      audio,
      data: { crayonDrawing }
    } = this.props.currentRound;
    if (!this.pointerSoundPlaying) {
      audio[crayonDrawing.src].play();
    }
    if (!this.helpInProgress) {
      // pause sound when instrument/mouse is not moving
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        audio[crayonDrawing.src].stop();
      }, 200);
    }
  }

  drawTextureCluster(clientX, clientY, guideLineIndex) {
    const diffX = clientX - this.previousCursorPosition.x;
    const diffY = clientY - this.previousCursorPosition.y;

    if (this.previousCursorPosition.x !== null && this.previousCursorPosition.y !== null) {
      for (let i = 1; i <= 3; i++) {
        this.drawTexturePoint(
          this.previousCursorPosition.x + (i / 3) * diffX,
          this.previousCursorPosition.y + (i / 3) * diffY,
          guideLineIndex
        );
      }
    }

    this.setPreviousCursorPosition(clientX, clientY);
  }

  moveDrawInstrument(x, y) {
    const isChalkPointer = this.drawInstrumentStates[this.state.roundIndex].name === 'chalk2';

    this.currentDrawInstrument.bringToFront();

    this.currentDrawInstrument.position = isChalkPointer
      ? this.getNextChalkPointerPosition(this.currentDrawInstrument, x, y)
      : { x, y };

    this.props.handleAutoScroll(this.currentDrawInstrument.position.y, true);
  }

  validateDrawDirection(currentGuideLine, clientX, clientY) {
    const circle = new paper.Shape.Circle(new paper.Point(clientX, clientY), 10);

    for (let i = 0; i < this.flags.length; i++) {
      if (circle.intersects(this.flags[i].left || circle.intersects(this.flags[i].right))) {
        if (i !== this.formerFlagIndex) {
          if (currentGuideLine.isReversed) {
            if (this.formerFlagIndex < i && this.formerFlagIndex !== -1) {
              return false;
            }
          } else {
            if (this.formerFlagIndex > i) {
              return false;
            }
          }
          this.formerFlagIndex = i;
        }
      }
    }

    return true;
  }

  validateDrawStray(currentGuideLine, cursorX, cursorY, currentTolerance) {
    const offset = new paper.Point(cursorX, cursorY);
    const tangent = currentGuideLine.getNearestPoint(new paper.Point(cursorX, cursorY));

    return offset.isClose(tangent, currentTolerance);
  }

  catchEndOfPath(cursorPoint) {
    //catch end of path moment and force handle draw end
    if (this.endPositionIsValid(cursorPoint)) {
      this.handleDrawEnd();
    }
  }

  endPositionIsValid(cursorPoint) {
    let positionIsValid = null;
    let nearestLocation = null;
    const currentGuideLine = this.guideLineSVGs[this.guideLineIndex];

    if (currentGuideLine.isDot) {
      positionIsValid = true;
    } else {
      nearestLocation = this.endPathNormal.getNearestLocation(cursorPoint);
      positionIsValid = nearestLocation.distance < 10;
    }

    return positionIsValid;
  }

  advance() {
    this.props.sndCorrect.off();
    if (this.guideLineIndex < this.guideLineSVGs.length - 1) {
      this.nextPath();
    } else {
      if (this.state.roundIndex < this.drawInstrumentStates.length - 1) {
        this.nextRound();
      } else {
        this.drawEnabled = false;

        this.timeoutList.add(() => {
          this.toggleDrawInstrumentOpacity();
          this.props.done();
        }, 1000);
      }
    }
  }

  setNextPathState() {
    const {
      audio,
      data: { clink }
    } = this.props.currentRound;
    this.removeFlagsFromPath(this.guideLineSVGs[this.guideLineIndex]);
    this.guideLineIndex++;
    const currentGuideLine = this.guideLineSVGs[this.guideLineIndex];

    if (currentGuideLine.isDot) {
      this.drawnDotPath = false;
    }

    if (!currentGuideLine.isContinuePath) {
      this.consecutiveErrorsAmount = 0;
    } else {
      if (!this.helpInProgress && currentGuideLine.isSegmented) {
        this.addVisualCue('success');
        audio[clink?.src] && audio[clink.src].play();
      }
    }
    if (!this.guideLineSVGs[this.guideLineIndex].isTransportPath) {
      this.flags = this.placeFlagsOnPath(this.guideLineSVGs[this.guideLineIndex]);
      this.formerFlagIndex = this.getFlagIndex(this.guideLineSVGs[this.guideLineIndex], this.flags);
      this.endPathNormal = this.placeEndPathNormalOnPath(this.guideLineSVGs[this.guideLineIndex]);
    }
  }

  nextPath() {
    let timeoutBetweenPathsDrawing = 0;

    this.drawEnabled = false;

    this.timeoutList.add(() => {
      this.setNextPathState();
      this.resetDrawInstrumentPosition();
      this.setDrawInstrumentVisibility();
    }, timeoutBetweenPathsDrawing);
  }

  drawPath(guideLineSVG, timeInterval) {
    const direction = guideLineSVG.isReversed;
    const isTransportPath = guideLineSVG.isTransportPath;
    const length = guideLineSVG.data.coordinates.length - 1;
    let index = direction ? length : 0;
    let x = 0;
    let y = 0;

    if (isTransportPath) {
      this.currentDrawInstrument.opacity = 0.5;
    } else {
      this.currentDrawInstrument.opacity = 1;
    }
    if (!this.guideLineSVGs[this.guideLineIndex].isTransportPath) {
      this.playTraceSound();
    }

    return new Promise(resolve => {
      this.handbrake = setInterval(() => {
        if (this.cancelDrawAnimation) {
          clearInterval(this.handbrake);
          return;
        }

        if (direction) {
          if (index >= 0) {
            ({ x, y } = guideLineSVG.data.coordinates[index]);
            if (!isTransportPath) {
              this.drawTexturePoint(x, y, this.guideLineIndex);
            }
            this.moveDrawInstrument(x, y);
            index--;
          } else {
            clearInterval(this.handbrake);
            resolve();
          }
        } else {
          if (index <= length) {
            ({ x, y } = guideLineSVG.data.coordinates[index]);
            if (!isTransportPath) {
              this.drawTexturePoint(x, y, this.guideLineIndex);
            }
            this.moveDrawInstrument(x, y);
            index++;
          } else {
            clearInterval(this.handbrake);
            resolve();
          }
        }
      }, timeInterval);
    });
  }

  resetDrawInstrumentPosition() {
    let startPoint = null;
    let currentGuideLine = this.guideLineSVGs[this.guideLineIndex];

    if (currentGuideLine.isContinuePath && this.guideLineIndex !== 0) {
      currentGuideLine = this.guideLineSVGs[this.guideLineIndex - 1];
    }

    if (currentGuideLine.isDot) {
      this.moveDrawInstrument(
        currentGuideLine.getBounds().center.x,
        currentGuideLine.getBounds().center.y
      );
    } else {
      startPoint = this.getPathStartPoint(currentGuideLine);
      this.moveDrawInstrument(startPoint.x, startPoint.y);
    }

    this.props.resetAutoScroll();
  }

  getPathStartPoint(guideLineSVG) {
    let pathStart = null;

    if (guideLineSVG.isDot) {
      pathStart = new paper.Point(
        guideLineSVG.getBounds().center.x,
        guideLineSVG.getBounds().center.y
      );
    } else {
      if (!guideLineSVG.isReversed) {
        pathStart = guideLineSVG.getPointAt(0);
      } else {
        pathStart = guideLineSVG.getPointAt(guideLineSVG.length);
      }
    }

    return pathStart;
  }

  drawTexturePoint(x, y) {
    const currentDrawInstrumentState = this.drawInstrumentStates[this.state.roundIndex];
    const raster = new paper.Raster(`${currentDrawInstrumentState.name}-texture`);
    const { width: canvasWidth } = this._canvas.getBoundingClientRect();
    const textureRadius = (4.3 / 100) * canvasWidth;
    raster.setWidth(textureRadius);
    raster.setHeight(textureRadius);
    raster.scale(1); //scale degree
    raster.rotate(shuffle(this.textureDegrees)[0]);
    this.textureGroup.addChild(raster);

    raster.position = { x, y };

    if (this.textureTrails[this.guideLineIndex]) {
      this.textureTrails[this.guideLineIndex].push(raster);
    }
  }

  removeTouchEventListeners() {
    if (this._canvas) {
      this._canvas.removeEventListener('mousedown', this.handleTouchStart);
      this._canvas.removeEventListener('mousemove', this.handleDraw);
      this._canvas.removeEventListener('mouseup', this.handleTouchEnd);
      this._canvas.removeEventListener('touchstart', this.handleTouchingStart);
      this._canvas.removeEventListener('touchmove', this.handleTouchDraw);
      this._canvas.removeEventListener('touchend', this.handleTouchingEnd);
      this._canvas.removeEventListener('mouseout', this.handleOutOfCanvasBounds);
    }
  }

  initializeTextureGroup(isFullscreen) {
    const { height, width } = this._canvas.getBoundingClientRect();
    const mask = new paper.Rectangle(
      isFullscreen ? 0 : (15 / 100) * width,
      isFullscreen ? 0 : (11.5 / 100) * height,
      isFullscreen ? width : (70 / 100) * width,
      isFullscreen ? height : height - (23.25 / 100) * height
    );
    const maskShape = new paper.Shape.Rectangle(mask);
    const textureGroup = new paper.Group([maskShape]);

    textureGroup.clipped = true;

    return textureGroup;
  }

  getScaleProportion() {
    let scaleProportion = 1;
    const iPadWidth = 1536 / 2;
    let currentWidth = this._container.getBoundingClientRect().width;

    scaleProportion = currentWidth / iPadWidth;

    return scaleProportion;
  }

  computeTolerance() {
    let staticTolerance = 1.55 * 32;
    let scaleProportion = this.getScaleProportion();

    this.currentTolerance = staticTolerance * scaleProportion * this.props.zoomToleranceFactor;
  }

  nextRound() {
    this.drawEnabled = false;

    if (this.state.roundIndex > 0) {
      this.removeTouchEventListeners();
    }

    this.timeoutList.add(() => {
      this.setState(state => ({ ...state, roundIndex: this.state.roundIndex + 1 }));
      this.computeTolerance();
    }, 1000);
  }

  setNextRoundState() {
    if (this.currentDrawInstrument) {
      this.currentDrawInstrument.remove();
    }
    if (this.state.roundIndex > 0) {
      this.removeFlagsFromPath(this.guideLineSVGs[this.guideLineIndex]);
    }
    this.guideLineIndex = 0;
    this.consecutiveErrorsAmount = 0;
    this.currentDrawInstrument = this.initializePointer();
    this.toggleDrawInstrumentOpacity(false);
    this.setDrawInstrumentVisibility();
    this.textureTrails = this.getTextureTrailState(this.guideLineSVGs);
    this.flags = this.placeFlagsOnPath(this.guideLineSVGs[this.guideLineIndex]);
    this.formerFlagIndex = this.getFlagIndex(this.guideLineSVGs[this.guideLineIndex], this.flags);
    this.endPathNormal = this.placeEndPathNormalOnPath(this.guideLineSVGs[this.guideLineIndex]);
  }

  initializePointer() {
    const currentGuideLine = this.guideLineSVGs[this.guideLineIndex];
    const pointer = new paper.Raster('chalk2-pointer');

    const { width: canvasWidth } = this._canvas.getBoundingClientRect();
    const textureRadius = Math.floor((11 / 100) * canvasWidth);
    let startPoint = null;

    pointer.opacity = 0;
    pointer.onLoad = () => {
      startPoint = this.getPathStartPoint(currentGuideLine);
      pointer.setWidth(textureRadius + 72);
      pointer.setHeight(textureRadius + 72);
      pointer.scale(1);
      pointer.smoothing = true;
      pointer.position = this.getNextChalkPointerPosition(pointer, startPoint.x, startPoint.y);
      pointer.opacity = 1;
      pointer.originalScale = pointer.getScaling().length;
    };
    return pointer;
  }

  placeFlagsOnPoint(guideLineSVG, pointIndex, flagVectorLength, step) {
    const currentPoint = guideLineSVG.getPointAt(pointIndex);
    const normalVector = guideLineSVG.getNormalAt(pointIndex);
    const guideLineLength = Math.floor(guideLineSVG.length);
    let right = null;
    let left = null;

    normalVector.setLength(flagVectorLength);

    right = new paper.Path({
      segments: [
        currentPoint,
        new paper.Point(currentPoint.x + normalVector.x, currentPoint.y + normalVector.y)
      ],
      strokeColor: 'red',
      opacity: this.flagOpacity,
      strokeWidth: 20
    });
    right.flagIndex =
      pointIndex === guideLineLength
        ? (guideLineLength - (guideLineLength % step)) / step + 1
        : pointIndex / step;
    right.parentGuideLineName = guideLineSVG.name;

    left = new paper.Path({
      segments: [
        currentPoint,
        new paper.Point(currentPoint.x - normalVector.x, currentPoint.y - normalVector.y)
      ],
      strokeColor: 'blue',
      opacity: this.flagOpacity,
      strokeWidth: 20
    });
    right.flagIndex =
      pointIndex === guideLineLength
        ? (guideLineLength - (guideLineLength % step)) / step + 1
        : pointIndex / step;
    left.parentGuideLineName = guideLineSVG.name;

    return {
      left,
      right
    };
  }

  getNextChalkPointerPosition(chalkPointer, currentX, currentY) {
    return {
      x: currentX + chalkPointer.bounds.width / 4 + 12,
      y: currentY + chalkPointer.bounds.height / 4 + 30
    };
  }

  setDrawInstrumentVisibility() {
    if (this.props.level === 1) {
      this.toggleDrawInstrumentOpacity(true);
    } else if (this.props.level === 3) {
      this.setBlinkingInstrument();
    } else if (this.props.level === 2) {
      this.toggleDrawInstrumentOpacity(true);
    }
  }

  setBlinkingInstrument() {
    this.currentDrawInstrument.opacity = 1;
    this.timeoutList.add(() => {
      this.currentDrawInstrument.opacity = 0;
    }, 1000);
    clearInterval(this.blinkingInstrument);
    this.blinkingInstrument = setInterval(() => {
      this.currentDrawInstrument.opacity = 1;
      this.timeoutList.add(() => {
        this.currentDrawInstrument.opacity = 0;
      }, 1000);
    }, 4000);
  }

  toggleDrawInstrumentOpacity(show) {
    this.currentDrawInstrument.opacity = show ? 1 : 0;
  }

  getTextureTrailState(guideLineSVGs) {
    const textureTrails = [];

    guideLineSVGs.forEach(guideLineSVG => {
      if (!guideLineSVG.isTransportPath) {
        textureTrails.push([]);
      } else {
        textureTrails.push(null);
      }
    });

    return textureTrails;
  }

  placeFlagsOnPath(guideLineSVG, step = 50) {
    const flags = [];
    const guideLineLength = Math.floor(guideLineSVG.length);

    if (!guideLineSVG.isDot) {
      for (let i = 0; i < guideLineLength; i += step) {
        flags.push(this.placeFlagsOnPoint(guideLineSVG, i, this.currentTolerance, step));
      }
    }
    return flags;
  }

  placeEndPathNormalOnPath(currentGuideLine) {
    let normalVector = null;
    let endPathNormal = null;

    if (this.endPathNormal) {
      this.endPathNormal.remove();
    }

    this.pathEndPoint = this.getPathEndPoint(currentGuideLine);

    if (!currentGuideLine.isDot) {
      if (currentGuideLine.isReversed) {
        normalVector = currentGuideLine.getNormalAt(0);
      } else {
        normalVector = currentGuideLine.getNormalAt(currentGuideLine.length);
      }

      normalVector.setLength(this.currentTolerance);

      endPathNormal = new paper.Path({
        segments: [
          new paper.Point(
            this.pathEndPoint.x - normalVector.x,
            this.pathEndPoint.y - normalVector.y
          ),
          new paper.Point(
            this.pathEndPoint.x + normalVector.x,
            this.pathEndPoint.y + normalVector.y
          )
        ],
        strokeColor: 'red',
        opacity: this.flagOpacity,
        strokeWidth: 1
      });
    }

    return endPathNormal;
  }

  getPathEndPoint(guideLineSVG) {
    let pathEnd = null;

    if (guideLineSVG.isDot) {
      pathEnd = new paper.Point(
        guideLineSVG.getBounds().center.x,
        guideLineSVG.getBounds().center.y
      );
    } else {
      if (guideLineSVG.isReversed) {
        pathEnd = guideLineSVG.getPointAt(0);
      } else {
        pathEnd = guideLineSVG.getPointAt(guideLineSVG.length);
      }
    }

    return pathEnd;
  }

  calculateHelperAdjustments() {
    const {
      _canvas: { width, height }
    } = this;

    const x = width - (95 / 100) * width;
    const y = (12.5 / 100) * height;

    return { x, y };
  }

  setHelperSmiley({ x, y }, show = true) {
    const { width: canvasWidth } = this._canvas.getBoundingClientRect();
    let textureRadius = Math.floor((11 / 100) * canvasWidth * 0.8);

    textureRadius = textureRadius > 90 ? textureRadius : 90;

    const helperAdjustments = this.calculateHelperAdjustments();

    this.setState(state => ({
      ...state,
      helpers: {
        ...state.helpers,
        smiley: {
          show,
          x: x - (helperAdjustments.x + textureRadius / 4),
          y: y - (helperAdjustments.y - textureRadius / 4),
          width: textureRadius,
          height: textureRadius
        }
      }
    }));
  }

  setHelperArrow({ x, y }, show = false) {
    const { width: canvasWidth } = this._canvas.getBoundingClientRect();
    let textureRadius = Math.floor((11 / 100) * canvasWidth * 0.8);

    textureRadius = textureRadius > 90 ? textureRadius : 90;

    const helperAdjustments = this.calculateHelperAdjustments();

    this.setState(state => ({
      ...state,
      helpers: {
        ...state.helpers,
        arrow: {
          show,
          x: x - (helperAdjustments.x + textureRadius / 4),
          y: y - (helperAdjustments.y - textureRadius / 4 - 20),
          width: textureRadius,
          height: textureRadius
        }
      }
    }));
  }

  renderCanvasLayers() {
    return this.drawInstrumentStates.map((drawInstrumentState, index) => {
      return (
        <canvas
          className={`${index <= this.state.roundIndex ? 'visible' : ''}`}
          id="canvas-game"
          key={index}
          ref={canvasEl => {
            if (this.state.roundIndex === index) {
              this._canvas = canvasEl;
            }
          }}
        />
      );
    });
  }

  renderDrawInstruments() {
    return (
      <div className="draw-instruments">
        {this.drawInstrumentStates.map((drawInstrumentState, index) => {
          return (
            <div className={drawInstrumentState.name} key={index}>
              <img
                id={`${drawInstrumentState.name}-texture`}
                src={drawInstrumentState.texturePath}
                alt={t('CrayonTexture')}
              />
              <img
                id={`${drawInstrumentState.name}-pointer`}
                src={drawInstrumentState.pointerPath}
                alt={t('CrayonPointer')}
              />
            </div>
          );
        })}
      </div>
    );
  }

  renderHelpers() {
    const {
      state: {
        helpers: { arrow, smiley }
      }
    } = this;

    return (
      <>
        {arrow.show && (
          <div
            className="crayon-stroke-btn-overlay"
            style={{ top: arrow.y, left: arrow.x, width: arrow.width, height: arrow.height }}
          >
            <img id="arrow-pointer2" src={arrowPointerPath} alt={t('ArrowPointer')} />
          </div>
        )}
        {smiley.show && (
          <div
            className="crayon-stroke-btn-overlay"
            style={{ top: smiley.y, left: smiley.x, width: smiley.width, height: smiley.height }}
          >
            <img id="smiley-pointer2" src={smileyPointerPath} alt={t('SmileyPointer')} />
          </div>
        )}
      </>
    );
  }

  render() {
    return (
      <div
        className="draw-activity-stage game"
        id="draw-game"
        ref={divEl => (this._container = divEl)}
      >
        {this.renderCanvasLayers()}
        {this.renderDrawInstruments()}
        {this.renderHelpers()}
      </div>
    );
  }
}

CrayonStrokeGame.propTypes = {
  sndCorrect: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  sndWrong: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  isMobile: PropTypes.bool,
  character: PropTypes.string,
  level: PropTypes.number,
  currentRound: PropTypes.shape({
    activityType: PropTypes.string,
    images: PropTypes.array,
    audio: PropTypes.array,
    data: PropTypes.shape({
      instructions: PropTypes.shape({
        beforeDraw: PropTypes.array
      }),
      crayonDrawing: PropTypes.object,
      clink: PropTypes.object
    })
  }),
  logError: PropTypes.func,
  onRoundEnd: PropTypes.func,
  done: PropTypes.func,
  inactivityManager: PropTypes.object,
  svgNameList: PropTypes.object,
  showVisualCue: PropTypes.func,
  hideVisualCue: PropTypes.func,
  segments: PropTypes.array,
  svgSrc: PropTypes.string,
  zoomToleranceFactor: PropTypes.number,
  handleAutoScroll: PropTypes.func,
  resetAutoScroll: PropTypes.func,
  helpers: PropTypes.object
};

const dummyHowl = {
  play: () => {},
  stop: () => {},
  on: (_, callback) => callback(),
  off: () => {}
};

const mapStateToProps = state => ({
  sndCorrect: state.ui.sounds.common.correct || dummyHowl,
  sndWrong: state.ui.sounds.common.wrong || dummyHowl,
  isMobile: state.ui.isMobile,
  character: state.activity.data.character.character,
  helpers: state.activity.helpers
});

const mapDispatchToProps = {
  showVisualCue,
  hideVisualCue
};

export default connect(mapStateToProps, mapDispatchToProps)(CrayonStrokeGame);
